import { useState, useEffect, useRef, useCallback } from "react";
import { loadFromSession, saveToSession, removeFromSession } from "@/utils/storage";
import { Column, ColumnState } from "@/components/table/DataTable";

interface UseDataTableLogicProps<T, F, C> {
  storageKey?: string;
  initialColumns: Column<T>[];
  defaultSortColumn?: string;
  defaultSortOrder?: "ASC" | "DESC";
  onFetchData?: (criteria: C) => Promise<void> | void;
  onFetchFilters?: () => Promise<F>;
  pagination?: any;
  autoRefreshInterval?: number; // Nova prop
}

export function useDataTableLogic<T, F, C>({
  storageKey,
  initialColumns,
  defaultSortColumn,
  defaultSortOrder = "ASC",
  onFetchData,
  onFetchFilters,
  pagination,
  autoRefreshInterval,
}: UseDataTableLogicProps<T, F, C>) {
  const [storedState] = useState(() => {
    if (!storageKey) return null;
    return loadFromSession(storageKey, {
      searchTerm: "",
      filterCriteria: null,
      sortColumn: defaultSortColumn || null,
      sortOrder: defaultSortOrder,
      visibleColumnKeys: initialColumns
        .filter((c) => c.visible !== false || c.key === "select")
        .map((c) => c.key),
      columnWidths: {} as Record<string, number>,
    });
  });

  const [isLoading, setIsLoading] = useState(false);
  const [columnStates, setColumnStates] = useState<ColumnState<T>[]>(() =>
    initialColumns.map((col) => ({
      ...col,
      isVisible: storedState?.visibleColumnKeys?.includes(col.key) ?? col.visible !== false,
    })),
  );
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    storedState?.columnWidths ?? {},
  );
  const [filterCriteria, setFilterCriteria] = useState<F | null>(
    storedState?.filterCriteria || null,
  );
  const [availableFilters, setAvailableFilters] = useState<F | null>(null);
  const [searchTerm, setSearchTerm] = useState(storedState?.searchTerm || "");

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [sortColumn, setSortColumn] = useState<string | null>(storedState?.sortColumn || null);
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">(storedState?.sortOrder || "ASC");

  const onFetchDataRef = useRef(onFetchData);
  const previousCriteriaRef = useRef<string | null>(null);

  const previousFilterSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    onFetchDataRef.current = onFetchData;
  }, [onFetchData]);

  useEffect(() => {
    setColumnStates((prev) =>
      initialColumns.map((col) => {
        const existingState = prev.find((p) => p.key === col.key);
        return {
          ...col,
          isVisible: existingState ? existingState.isVisible : col.visible !== false,
        };
      }),
    );
  }, [initialColumns]);

  useEffect(() => {
    if (onFetchFilters) {
      onFetchFilters().then(setAvailableFilters).catch(console.error);
    }
  }, [onFetchFilters]);

  useEffect(() => {
    if (!storageKey) return;
    const currentState = {
      searchTerm,
      filterCriteria,
      sortColumn,
      sortOrder,
      visibleColumnKeys: columnStates.filter((c) => c.isVisible).map((c) => c.key),
      columnWidths,
    };
    saveToSession(storageKey, currentState);
  }, [storageKey, searchTerm, filterCriteria, sortColumn, sortOrder, columnStates, columnWidths]);

  useEffect(() => {
    if (searchTerm === "") {
      setDebouncedSearchTerm("");
      return;
    }
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const executeFetch = useCallback(
    async (isAutoRefresh = false) => {
      const fetchFn = onFetchDataRef.current;
      if (!fetchFn) return;

      const searchNormalized = debouncedSearchTerm.trim();
      const search = searchNormalized.length > 0 ? searchNormalized : null;

      const filterSignature = JSON.stringify({ filter: filterCriteria || {}, search });
      const filterChanged =
        previousFilterSignatureRef.current !== null &&
        previousFilterSignatureRef.current !== filterSignature;

      const currentPage = pagination?.page ?? null;
      const effectivePage = filterChanged && currentPage && currentPage > 1 ? 1 : currentPage;

      const requestCriteria = {
        ...(filterCriteria || {}),
        search,
        sort: sortColumn,
        order: sortOrder,
        page: effectivePage,
        limit: pagination?.limit ?? null,
      } as unknown as C;

      // Lógica de "deep compare" para evitar loop infinito
      const serialized = JSON.stringify(requestCriteria);

      // Se for auto-refresh, ignora a checagem de "mudou alguma coisa", forçando o fetch.
      if (!isAutoRefresh && serialized === previousCriteriaRef.current) return;

      previousCriteriaRef.current = serialized;
      previousFilterSignatureRef.current = filterSignature;

      try {
        // Se for auto-refresh não mostramos loading para não piscar a tela
        if (!isAutoRefresh) setIsLoading(true);
        await fetchFn(requestCriteria);
      } finally {
        if (!isAutoRefresh) setIsLoading(false);
      }
    },
    [
      filterCriteria,
      debouncedSearchTerm,
      sortColumn,
      sortOrder,
      pagination?.page,
      pagination?.limit,
    ],
  );

  useEffect(() => {
    executeFetch(false);
  }, [executeFetch]);

  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      executeFetch(true); // true = é autoRefresh (não gera loading state global)
    }, autoRefreshInterval);

    return () => clearInterval(intervalId);
  }, [executeFetch, autoRefreshInterval]);

  const handleClearAll = useCallback(() => {
    if (storageKey) removeFromSession(storageKey);

    setSearchTerm("");
    setSortColumn(defaultSortColumn || null);
    setSortOrder(defaultSortOrder);

    setFilterCriteria(null);
    setColumnWidths({});
    setColumnStates(initialColumns.map((c) => ({ ...c, isVisible: c.visible !== false })));
  }, [storageKey, defaultSortColumn, defaultSortOrder, initialColumns]);

  return {
    state: {
      isLoading,
      searchTerm,
      filterCriteria,
      availableFilters,
      columnStates,
      columnWidths,
      sortColumn,
      sortOrder,
    },
    actions: {
      setSearchTerm,
      setFilterCriteria,
      setColumnStates,
      setColumnWidths,
      setSortColumn,
      setSortOrder,
      handleClearAll,
    },
  };
}
